import { component, computed, observable } from 'knockout-decorators'

import { EventState } from '~/store/event'
import { ReservationState } from '~/store/reservation'

import type { EventConfig } from '@tixa/schema'
import { Tracker, Locale } from '~/utils'
import type { SeatMapConfig } from '../seatmap'
import { SeatMap } from '../seatmap'

import source from './reservation-ticket.html?raw'

const VIEWPORT_NO_ZOOM =
  'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no'

let savedViewportContent: string | null = null

function disableViewportZoom(): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]')
  if (!meta) return
  const current = meta.getAttribute('content')
  if (current && current !== VIEWPORT_NO_ZOOM) {
    savedViewportContent = current
  }
  meta.setAttribute('content', VIEWPORT_NO_ZOOM)
}

function restoreViewportZoom(): void {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="viewport"]')
  if (!meta) return
  meta.setAttribute(
    'content',
    savedViewportContent ?? 'width=device-width, initial-scale=1.0'
  )
  savedViewportContent = null
}

@component('reservation-ticket', source)
export class ReservationTicket {
  private eventState: EventState = EventState.get()
  private reservationState: ReservationState
  private seatmap: SeatMap | null = null

  private seatMapConfig: SeatMapConfig = {
    clickZoomHandler: null,
    confirmMessageHandler: null,
    disableZoom: false,
    errorMessageHandler: (message) => {
      this.eventState.globalError = message
      this.reservationState.seatmapValidationError = message
    },
    lang: Locale.language,
    seatMapMode: 1,
    sendTicketsHandler: (toSend) => {
      // Hide seatmap first to avoid modal conflicts
      this.hideSeatmap()
      this.reservationState.seatmapReservations = toSend
    },
    sendTicketsErrorHandler: (error) => {
      this.reservationState.seatmapValidationError = error
    },
  }

  constructor(private params: { eventId: string; seatmapId: string }) {
    this.reservationState =
      this.eventState.reservationStates[this.params.seatmapId]
    this.initSeatmap()
  }

  @observable
  private isSeatmapLoading = false

  @observable
  private isSeatmapClosed = false

  @observable
  private hasSeatmapExpired = false

  @computed
  get isAvailable(): boolean {
    return this.reservationState.isAvailable
  }

  @computed
  get isSeatmapOpen(): boolean {
    return this.reservationState.isSeatmapOpen
  }

  @computed
  get eventConfig(): EventConfig | undefined {
    return this.eventState.events.find(
      (event) =>
        event.eventID.toString() === this.params.eventId.toString() &&
        event.seatMapID?.toString() === this.params.seatmapId.toString()
    )
  }

  @computed
  get groupLabel1(): string {
    return this.eventConfig?.seatMapGroup1 || ''
  }

  @computed
  get groupLabel2(): string {
    return this.eventConfig?.seatMapGroup2 || ''
  }

  private async initSeatmap() {
    const { SeatMap } = await import(
      /* webpackChunkName: "seatmap" */ '../seatmap'
    )
    if (null === this.seatmap || this.hasSeatmapExpired) {
      this.seatmap = new SeatMap()
      const seatMapInternal = await this.seatmap.init(
        this.params.eventId,
        this.params.seatmapId,
        this.seatMapConfig
      )
      this.reservationState.seatmap = seatMapInternal
    }
  }

  private async showSeatmap() {
    if (Tracker && typeof Tracker.openedSeatmap === 'function') {
      Tracker.openedSeatmap()
    }
    this.reservationState.isSeatmapOpen = true
    if (!this.seatmap || this.hasSeatmapExpired) {
      this.isSeatmapLoading = true
      this.isSeatmapClosed = false
      await this.initSeatmap()
      this.isSeatmapLoading = false
    }
    document.body.classList.add('seat-map-modal-open')
    disableViewportZoom()
    if (this.seatmap) this.seatmap.open()
  }

  private hideSeatmap() {
    this.reservationState.seatmap.disposeSeatMap()
    this.hasSeatmapExpired = true
    this.reservationState.isSeatmapOpen = false
    this.isSeatmapClosed = true
    document.body.classList.remove('seat-map-modal-open')
    restoreViewportZoom()
  }
}
